import React, { createContext, useReducer } from "react";
import PropTypes from "prop-types";
import jwt_decode from "jwt-decode";
import tokenProvider from "core/tokenProvider";
import {
  getHistory,
  addHistoryEntry,
  deleteEntry,
  clearHistory as clearStoredHistory,
} from "services/notificationHistoryService";

/* Actions Types */
const SET_NOTIF_SUCCESS = "SET_NOTIF_SUCCESS";
const SET_NOTIF_ERROR = "SET_NOTIF_ERROR";
const SET_NOTIF_WARNING = "SET_NOTIF_WARNING";
const SET_NOTIF_INFO = "SET_NOTIF_INFO";
const SHOW_NOTIF = "SHOW_NOTIF";
const HIDE_NOTIF = "HIDE_NOTIF";
const RESET_NOTIF = "RESET_NOTIF";
const TOGGLE_HISTORY_PANEL = "TOGGLE_HISTORY_PANEL";
const DELETE_HISTORY_ENTRY = "DELETE_HISTORY_ENTRY";
const CLEAR_HISTORY = "CLEAR_HISTORY";

export const AppStateContext = createContext();
export const AppDispatchContext = createContext();

const decodedJwt =
  tokenProvider.getToken() && jwt_decode(tokenProvider.getToken());

const initialState = {
  user: {
    profile: decodedJwt?.profile,
    identity: decodedJwt?.identity,
    id: decodedJwt?.sub,
    canAccessBilling: decodedJwt?.can_access_billing === true,
  },
  notif: {
    show: null,
    success: null,
    error: null,
    warning: null,
    info: null,
  },
  toastHistory: {
    isOpen: false,
    items: getHistory(),
  },
};

const appReducer = (state, action) => {
  switch (action.type) {
    case SET_NOTIF_SUCCESS: {
      addHistoryEntry({
        message: action.payload,
        type: "success",
      });
      return {
        ...state,
        notif: { ...state.notif, show: true, success: action.payload },
        toastHistory: {
          ...state.toastHistory,
          items: getHistory(),
        },
      };
    }
    case SET_NOTIF_ERROR: {
      addHistoryEntry({ message: action.payload, type: "error" });
      return {
        ...state,
        notif: { ...state.notif, show: true, error: action.payload },
        toastHistory: {
          ...state.toastHistory,
          items: getHistory(),
        },
      };
    }
    case SET_NOTIF_WARNING: {
      addHistoryEntry({
        message: action.payload,
        type: "warning",
      });
      return {
        ...state,
        notif: { ...state.notif, show: true, warning: action.payload },
        toastHistory: {
          ...state.toastHistory,
          items: getHistory(),
        },
      };
    }
    case SET_NOTIF_INFO: {
      addHistoryEntry({ message: action.payload, type: "info" });
      return {
        ...state,
        notif: { ...state.notif, show: true, info: action.payload },
        toastHistory: {
          ...state.toastHistory,
          items: getHistory(),
        },
      };
    }
    case SHOW_NOTIF:
      return {
        ...state,
        notif: { ...state.notif, show: true },
      };
    case HIDE_NOTIF:
      return {
        ...state,
        notif: { ...state.notif, show: false },
      };
    case RESET_NOTIF:
      return {
        ...state,
        notif: {
          show: null,
          success: null,
          error: null,
          warning: null,
          info: null,
        },
      };
    case TOGGLE_HISTORY_PANEL:
      return {
        ...state,
        toastHistory: {
          ...state.toastHistory,
          isOpen:
            action.payload !== undefined
              ? action.payload
              : !state.toastHistory.isOpen,
        },
      };
    case DELETE_HISTORY_ENTRY:
      deleteEntry(action.payload);
      return {
        ...state,
        toastHistory: {
          ...state.toastHistory,
          items: getHistory(),
        },
      };
    case CLEAR_HISTORY:
      clearStoredHistory();
      return {
        ...state,
        toastHistory: {
          ...state.toastHistory,
          items: [],
        },
      };
    default:
      return state;
  }
};

const AppContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

AppContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default AppContextProvider;
